import { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';

export interface DateRangePickerProps {
  show: boolean;
  onClose: () => void;
  onApply: (startDate: number | null, endDate: number | null) => void;
  isLoading?: boolean;
}

/**
 * Modal component for selecting a date range to filter dashboard data.
 */
export function DateRangePicker({ show, onClose, onApply, isLoading = false }: DateRangePickerProps) {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const handleApply = () => {
    const start = startDate?.unix() ?? null;
    const end = endDate?.unix() ?? null;
    onApply(start, end);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Select a Date Range for Viewing Data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Choose a start and end date to filter the data displayed in the dashboard.
        </p>
        <div className="d-flex align-items-center gap-2">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disabled={isLoading}
              value={startDate}
              onChange={(value) => setStartDate(value)}
            />
          </LocalizationProvider>
          <i className="bi bi-dash-lg"></i>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disabled={isLoading}
              value={endDate}
              onChange={(value) => setEndDate(value)}
            />
          </LocalizationProvider>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApply} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Fetching Data...</span>
            </>
          ) : (
            <>Apply Range</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
